import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, Edit, Trash2, ArrowLeft, Play, DollarSign } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DyvysAdminEpisodesPage() {
  const { seriesId } = useParams();
  const [series, setSeries] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingEpisode, setEditingEpisode] = useState(null);
  const [formData, setFormData] = useState({
    episode_number: 1,
    title: "",
    description: "",
    video_url: "",
    video_type: "youtube",
    thumbnail_url: "",
    duration_minutes: "",
    price_usd: 0.5,
    price_cdf: 1000,
    is_free: false
  });
  const navigate = useNavigate();

  const adminPassword = localStorage.getItem("dyvys_admin_password");

  useEffect(() => {
    if (!adminPassword) {
      toast.error("Veuillez vous connecter");
      navigate("/dyvys/admin/login");
      return;
    }
    fetchSeriesAndEpisodes();
  }, [seriesId]);

  const fetchSeriesAndEpisodes = async () => {
    try {
      // Récupérer les infos de la série
      const seriesResponse = await fetch(`${API_URL}/api/dyvys/series/${seriesId}`);
      const seriesData = await seriesResponse.json();
      setSeries(seriesData.series);

      // Récupérer les épisodes
      const episodesResponse = await fetch(`${API_URL}/api/dyvys/admin/series/${seriesId}/episodes`, {
        headers: {
          "X-Admin-Password": adminPassword
        }
      });

      if (!episodesResponse.ok) throw new Error("Erreur de chargement");

      const episodesData = await episodesResponse.json();
      setEpisodes(episodesData.episodes || []);
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (episode = null) => {
    if (episode) {
      setEditingEpisode(episode);
      setFormData({
        episode_number: episode.episode_number,
        title: episode.title,
        description: episode.description || "",
        video_url: episode.video_url,
        video_type: episode.video_type || "youtube",
        thumbnail_url: episode.thumbnail_url || "",
        duration_minutes: episode.duration_minutes || "",
        price_usd: episode.price_usd || 0.5,
        price_cdf: episode.price_cdf || 1000,
        is_free: episode.is_free || false
      });
    } else {
      setEditingEpisode(null);
      const nextEpisodeNumber = episodes.length > 0 
        ? Math.max(...episodes.map(e => e.episode_number)) + 1 
        : 1;
      setFormData({
        episode_number: nextEpisodeNumber,
        title: "",
        description: "",
        video_url: "",
        video_type: "youtube",
        thumbnail_url: "",
        duration_minutes: "",
        price_usd: 0.5,
        price_cdf: 1000,
        is_free: false
      });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData,
        series_id: seriesId,
        duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : null,
        price_usd: parseFloat(formData.price_usd),
        price_cdf: parseInt(formData.price_cdf)
      };

      const url = editingEpisode
        ? `${API_URL}/api/dyvys/admin/episodes/${editingEpisode.episode_id}`
        : `${API_URL}/api/dyvys/admin/episodes`;

      const method = editingEpisode ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Password": adminPassword
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error("Erreur de sauvegarde");

      toast.success(editingEpisode ? "Épisode mis à jour!" : "Épisode créé!");
      setShowDialog(false);
      fetchSeriesAndEpisodes();
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleDelete = async (episodeId) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cet épisode ?")) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/dyvys/admin/episodes/${episodeId}`, {
        method: "DELETE",
        headers: {
          "X-Admin-Password": adminPassword
        }
      });

      if (!response.ok) throw new Error("Erreur de suppression");

      toast.success("Épisode supprimé!");
      fetchSeriesAndEpisodes();
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-lg">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-black text-white">
      {/* Header */}
      <header className="border-b border-purple-800/50 backdrop-blur-sm bg-black/30">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <Button
                onClick={() => navigate("/dyvys/admin/series")}
                variant="ghost"
                className="text-purple-300 hover:text-purple-100 hover:bg-purple-500/20 mb-2"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour aux séries
              </Button>
              <h1 className="text-3xl font-bold text-white">{series?.title}</h1>
              <p className="text-gray-400">Gestion des Épisodes</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Actions */}
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl font-bold">Tous les Épisodes ({episodes.length})</h2>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvel Épisode
          </Button>
        </div>

        {/* Episodes List */}
        {episodes.length === 0 ? (
          <div className="text-center py-20">
            <Play className="w-16 h-16 text-purple-400 mx-auto mb-4 opacity-50" />
            <p className="text-xl text-gray-400">Aucun épisode créé</p>
            <p className="text-gray-500 mt-2">Cliquez sur "Nouvel Épisode" pour commencer</p>
          </div>
        ) : (
          <div className="space-y-4">
            {episodes.map((episode) => (
              <Card
                key={episode.episode_id}
                className="bg-gradient-to-r from-gray-800/50 to-purple-900/30 border-purple-500/30"
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-6">
                    {/* Episode Thumbnail */}
                    <div className="flex-shrink-0">
                      {episode.thumbnail_url ? (
                        <img
                          src={episode.thumbnail_url}
                          alt={episode.title}
                          className="w-32 h-20 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-32 h-20 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg flex items-center justify-center">
                          <Play className="w-8 h-8 text-white opacity-50" />
                        </div>
                      )}
                    </div>

                    {/* Episode Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-purple-400 font-bold">Épisode {episode.episode_number}</span>
                        {episode.is_free && (
                          <span className="bg-green-500/20 text-green-300 text-xs px-2 py-1 rounded">
                            Gratuit
                          </span>
                        )}
                        {!episode.is_active && (
                          <span className="bg-red-500/20 text-red-300 text-xs px-2 py-1 rounded">
                            Inactif
                          </span>
                        )}
                      </div>
                      <h3 className="text-xl font-semibold text-white mb-1">{episode.title}</h3>
                      {episode.description && (
                        <p className="text-gray-400 text-sm line-clamp-2 mb-2">{episode.description}</p>
                      )}
                      <div className="flex items-center gap-4 text-sm text-gray-400">
                        <span className="flex items-center gap-1">
                          <Play className="w-4 h-4" />
                          {episode.video_type === "youtube" ? "YouTube" : "Upload"}
                        </span>
                        {episode.duration_minutes && (
                          <span>{episode.duration_minutes} min</span>
                        )}
                        {!episode.is_free && (
                          <span className="flex items-center gap-1 text-green-400">
                            <DollarSign className="w-4 h-4" />
                            {episode.price_cdf} CDF
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-blue-500 text-blue-300 hover:bg-blue-500/20"
                        onClick={() => handleOpenDialog(episode)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-500 text-red-300 hover:bg-red-500/20"
                        onClick={() => handleDelete(episode.episode_id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-gray-900 text-white border-purple-500/30 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {editingEpisode ? "Modifier l'épisode" : "Nouvel épisode"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="episode_number">Numéro d'épisode *</Label>
                  <Input
                    id="episode_number"
                    type="number"
                    min="1"
                    value={formData.episode_number}
                    onChange={(e) => setFormData({ ...formData, episode_number: parseInt(e.target.value) })}
                    className="bg-gray-800 border-purple-500/30 text-white"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="duration">Durée (minutes)</Label>
                  <Input
                    id="duration"
                    type="number"
                    min="1"
                    placeholder="Ex: 45"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
                    className="bg-gray-800 border-purple-500/30 text-white"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">Titre de l'épisode *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="bg-gray-800 border-purple-500/30 text-white"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  rows={3}
                  placeholder="Décrivez l'épisode..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-gray-800 border-purple-500/30 text-white"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="video_type">Type de vidéo *</Label>
                <Select
                  value={formData.video_type}
                  onValueChange={(value) => setFormData({ ...formData, video_type: value })}
                >
                  <SelectTrigger className="bg-gray-800 border-purple-500/30 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-purple-500/30 text-white">
                    <SelectItem value="youtube">YouTube</SelectItem>
                    <SelectItem value="upload">Fichier uploadé</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="video_url">
                  {formData.video_type === "youtube" ? "URL YouTube *" : "URL de la vidéo *"}
                </Label>
                <Input
                  id="video_url"
                  type="url"
                  placeholder={formData.video_type === "youtube" ? "https://youtube.com/watch?v=..." : "https://..."}
                  value={formData.video_url}
                  onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                  className="bg-gray-800 border-purple-500/30 text-white"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="thumbnail">
                  🖼️ URL de l'image miniature (Thumbnail)
                </Label>
                <Input
                  id="thumbnail"
                  type="url"
                  placeholder="https://..."
                  value={formData.thumbnail_url}
                  onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                  className="bg-gray-800 border-purple-500/30 text-white"
                />
                <div className="bg-blue-500/10 border border-blue-500/30 rounded p-3 text-xs text-blue-300">
                  <p className="font-semibold mb-1">💡 Comment ajouter une miniature :</p>
                  <p className="mb-1">1. <strong>YouTube automatique :</strong> Laissez vide, YouTube génère automatiquement</p>
                  <p className="mb-1">2. <strong>Image personnalisée :</strong></p>
                  <p className="ml-4">• Uploadez votre image sur <a href="https://imgur.com" target="_blank" className="underline">Imgur.com</a> ou Facebook</p>
                  <p className="ml-4">• Copiez le lien de l'image</p>
                  <p className="ml-4">• Collez-le ici</p>
                </div>
              </div>

              <div className="flex items-center space-x-2 bg-gray-800/50 p-4 rounded-lg">
                <Switch
                  id="is_free"
                  checked={formData.is_free}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_free: checked })}
                />
                <Label htmlFor="is_free" className="cursor-pointer">
                  Épisode gratuit (pas de paiement requis)
                </Label>
              </div>

              {!formData.is_free && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="price_cdf">Prix (CDF) *</Label>
                    <Input
                      id="price_cdf"
                      type="number"
                      min="0"
                      value={formData.price_cdf}
                      onChange={(e) => setFormData({ ...formData, price_cdf: e.target.value })}
                      className="bg-gray-800 border-purple-500/30 text-white"
                      required={!formData.is_free}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="price_usd">Prix (USD) *</Label>
                    <Input
                      id="price_usd"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.price_usd}
                      onChange={(e) => setFormData({ ...formData, price_usd: e.target.value })}
                      className="bg-gray-800 border-purple-500/30 text-white"
                      required={!formData.is_free}
                    />
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
                className="border-gray-600 text-gray-300"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                {editingEpisode ? "Mettre à jour" : "Créer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
